import React, { useCallback, useMemo, useState } from "react";

import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";

import Box from "veneer/Box";
import { JollyRancherChip } from "veneer/Chip";
import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import Grid from "veneer/Grid";
import Link from "veneer/Link";
import TextInput from "veneer/TextInput";
import ToggleButtonGroup, { ToggleButton } from "veneer/ToggleButtonGroup";

import { ContentGroupItem } from "cortex/ui/src/fleet/view/media-library/model";

import DraggableContentList, { type ItemLike } from "./DraggableContentList";
import MediaUploadForm from "./MediaUploadForm";

type Mode = "select" | "upload";
const filter = createFilter<ContentGroupItem>("name", "tag", "type");
const column = createColumnHelper<ContentGroupItem>();

export interface ContentGroupCreateFormProps {
  contentGroupName?: string;
  onChangeName?: (name: string) => void;
  onUpdateContent?: (content: ReadonlyArray<ContentGroupItem>) => void;
  selectedContent?: ReadonlyArray<ContentGroupItem>;
  userContents?: ReadonlyArray<ContentGroupItem>;
}

export default function ContentGroupCreateForm({
  contentGroupName,
  onChangeName,
  onUpdateContent,
  selectedContent,
  userContents,
}: ContentGroupCreateFormProps) {
  const [mode, setMode] = useState<Mode>("select");

  const columns = useMemo(
    () => [
      column.accessor("name", {
        header: "Name",
        cell: (prop) => prop.getValue(),
        sortingFn: "alphanumeric",
      }),
      column.accessor("type", {
        header: "Type",
        cell: (prop) => prop.getValue() || "N/A",
        sortingFn: "alphanumeric",
        meta: {
          width: 150,
        },
      }),
      column.accessor("tag", {
        header: "Creative Category",
        cell: (prop) => {
          const tag = prop.getValue();
          if (!tag) {
            return null;
          }
          return <JollyRancherChip color="red" label={tag} />;
        },
        sortingFn: sorting.string((row) => row.tag || ""),
        meta: {
          width: 200,
        },
      }),
      column.display({
        header: "Preview",
        cell: (prop) => {
          const row = prop.row.original;
          const isVideo = row.type?.startsWith("video");
          const isImage = row.type?.startsWith("image");
          return (
            <Link newTab path={row.url}>
              {isVideo ? (
                <VideoCameraBackIcon />
              ) : isImage ? (
                <img
                  alt={row.name}
                  src={row.url}
                  style={{ height: 28, objectFit: "cover" }}
                />
              ) : null}
            </Link>
          );
        },
        meta: {
          width: 100,
        },
      }),
    ],
    [],
  );

  const onChangeMode = (next: Mode) => setMode((m) => (m === next ? m : next));

  const replaceContent = useCallback(
    (items: ReadonlyArray<ItemLike>) => {
      onUpdateContent?.(items as ReadonlyArray<ContentGroupItem>);
    },
    [onUpdateContent],
  );

  const deleteContent = useCallback(
    (item: ItemLike) => {
      const toDelete = new Set([item.id]);
      const updated = (selectedContent ?? []).filter(
        (c) => !toDelete.has(c.id),
      );
      onUpdateContent?.(updated);
    },
    [onUpdateContent, selectedContent],
  );

  const editDuration = useCallback(
    (duration: number, content: ItemLike) => {
      const updated = (selectedContent ?? []).map((c) =>
        c.id === content.id ? { ...c, duration } : c,
      );
      onUpdateContent?.(updated);
    },
    [onUpdateContent, selectedContent],
  );

  return (
    <Box>
      <Grid alignItems="flex-start" container spacing={2}>
        <Grid item md={4} xs={12}>
          <TextInput
            fullWidth
            label="Media Group Name"
            onChange={(value) => onChangeName?.(value ?? "")}
            required
            value={contentGroupName ?? ""}
          />
        </Grid>
        <Grid item md={8} xs={12}>
          <DraggableContentList
            items={selectedContent ?? []}
            onChangeContentDuration={editDuration}
            onDeleteContent={deleteContent}
            onReorderContent={replaceContent}
          />
        </Grid>
        <Grid item xs={12}>
          <ToggleButtonGroup
            fullWidth
            onChange={(value) => {
              if (value === "select" || value === "upload") {
                onChangeMode(value);
              }
            }}
          >
            <ToggleButton selected={mode === "upload"} value="upload">
              Upload Content
            </ToggleButton>
            <ToggleButton selected={mode === "select"} value="select">
              Select From Existing Content
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        {mode === "upload" && (
          <Grid item xs={12}>
            <MediaUploadForm />
          </Grid>
        )}
        {mode === "select" && (
          <Grid item xs={12}>
            <DataTable
              columns={columns}
              filter={filter}
              getIdentifier={({ id }) => String(id)}
              initialPageSize={5}
              paginationProps={{
                rowsPerPageOptions: [5, 10, 25],
              }}
              rows={userContents ?? []}
              selectedItems={selectedContent ?? []}
              setSelectedItems={(items) => onUpdateContent?.(items)}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
